import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PackageCardComponent } from '../../../package-card/package-card.component';
import { NoPackagesComponent } from '../../../../orders/components/no-packages/no-packages.component';
import { AddPackageModalComponent } from '../../../add-package-modal/add-package-modal.component';
import { PackageService } from '../../../../services/package.service';
import { RoleService } from '../../../../services/role.service';
import { LoggerService } from '../../../../services/logger.service';
import { Package } from '../../../../shared/types';
import { UserRole } from '../../../../shared/types/role.interface';
import {
  AddOrderStateService,
  WashTier,
  WashTierOption,
  WashOptionType,
} from '../../services/add-order-state.service';

export interface PackageSelection {
  tierId: string;
  optionId: string;
  isMonthly: boolean;
  isFamilyPlan: boolean;
}

/**
 * Step 1 — Package Selection.
 * Loads packages based on user role, displays them using the shared
 * PackageCardComponent, and emits the user's selection upward.
 *
 * Reuses: PackageCardComponent, NoPackagesComponent, AddPackageModalComponent.
 */
@Component({
  selector: 'app-package-step',
  standalone: true,
  imports: [CommonModule, PackageCardComponent, NoPackagesComponent, AddPackageModalComponent],
  templateUrl: './package-step.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./package-step.component.scss'],
})
export class PackageStepComponent implements OnInit, OnChanges, OnDestroy {
  /** When a specific package is pre-selected (e.g. from a parent context). */
  @Input() selectedPackage: Package | null = null;
  /** Title shown above the package list. */
  @Input() sectionTitle = 'Select Car Wash';

  @Output() selectionChange = new EventEmitter<PackageSelection>();
  @Output() cancel = new EventEmitter<void>();
  @Output() continueClicked = new EventEmitter<PackageSelection>();

  private readonly destroy$ = new Subject<void>();

  washTiers: WashTier[] = [];
  isLoadingPackages = false;
  selectedTierId = '';
  selectedOptionId = '';
  showAddPackageModal = false;

  constructor(
    private readonly packageService: PackageService,
    private readonly roleService: RoleService,
    private readonly logger: LoggerService,
    private readonly addOrderState: AddOrderStateService
  ) {}

  ngOnInit(): void {
    this.loadPackages();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPackage'] && this.selectedPackage) {
      this.loadPackages();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Gets the current package selection based on the selected tier and option.
   * @example
   * currentSelection()
   * { tierId: 1, optionId: 2, isMonthly: true, isFamilyPlan: false }
   * @returns {PackageSelection | null} The current package selection, or null if no option is selected or no matching option is found.
   **/
  get currentSelection(): PackageSelection | null {
    if (!this.selectedOptionId) return null;
    const option = this.findSelectedOption();
    return option
      ? {
          tierId: this.selectedTierId,
          optionId: this.selectedOptionId,
          isMonthly: option.type === 'monthly',
          isFamilyPlan: option.family_plan === true,
        }
      : null;
  }

  onPackageSelected(tierId: string, optionId: string): void {
    this.selectedTierId = tierId;
    this.selectedOptionId = optionId;
    const sel = this.currentSelection;
    if (sel) this.selectionChange.emit(sel);
  }

  onContinue(): void {
    const sel = this.currentSelection;
    if (sel) this.continueClicked.emit(sel);
  }

  onAddPackage(): void {
    this.showAddPackageModal = true;
  }

  onAddPackageModalClose(): void {
    this.showAddPackageModal = false;
    this.loadPackages();
  }

  trackByTier(_: number, tier: WashTier): string {
    return tier?.id ?? '';
  }

  // ── Package loading ────────────────────────────────────────────────────────

  /**
   * Loads car wash packages for the current user role and initializes package selection state.
   * @example
   * loadPackages()
   * void
   * @returns {void} Does not return a value.
   */
  loadPackages(): void {
    this.isLoadingPackages = true;
    const role = this.roleService.getCurrentUserRole();

    if ((role === UserRole.ATTENDANT || role === UserRole.ATTENDANT_PLUS) && this.selectedPackage) {
      this.isLoadingPackages = false;
      this.washTiers = this.transformToWashTiers([this.selectedPackage]);
      this.copyWashTiersToState();
      return;
    }

    const source$: Observable<Package[] | any> =
      role === UserRole.ATTENDANT || role === UserRole.ATTENDANT_PLUS
        ? this.packageService.getCarWashPackagesForStaff()
        : this.packageService.getCarWashPackages();

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.isLoadingPackages = false;
        const data: Package[] = Array.isArray(res)
          ? res
          : res?.data && Array.isArray(res.data)
            ? res.data
            : [];
        this.washTiers = this.transformToWashTiers(data);
        this.copyWashTiersToState();
      },
      error: (err: any) => {
        this.isLoadingPackages = false;
        this.washTiers = [];
        this.addOrderState.washTiers = [];
        this.logger.trackError('PackageStepComponent.loadPackages', err);
      },
    });
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Transforms a list of package objects into wash tier objects with valid pricing options.
   * @example
   * transformToWashTiers(packages)
   * [{ id: 1, name: 'Basic', features: [], options: [...] }]
   * @param {Package[]} packages - Array of package objects to convert into wash tiers.
   * @returns {WashTier[]} Array of wash tier objects with available options.
   **/
  private transformToWashTiers(packages: Package[]): WashTier[] {
    if (!packages?.length) return [];

    return packages
      .filter(pkg => pkg.package_status !== false)
      .map(pkg => {
        const options: WashTierOption[] = [];

        if (pkg.single_list_price && String(pkg.single_list_price) !== '0') {
          options.push(this.buildOption(pkg, 'single'));
        }
        if (pkg.monthly_list_price && String(pkg.monthly_list_price) !== '0') {
          options.push(this.buildOption(pkg, 'monthly'));
          if (pkg.family_plan) {
            options.push(this.buildOption(pkg, 'monthly', true));
          }
        }

        return {
          id: pkg.id,
          name: pkg.package_name,
          features: Array.isArray(pkg.features) ? pkg.features : [],
          options,
        } as WashTier;
      })
      .filter(tier => tier.options.length > 0);
  }

  /**
   * Builds a wash tier option object from a package, pricing type, and optional family plan flag.
   * @example
   * buildOption(pkg, 'monthly', true)
   * { id: '123-monthly-family', label: 'Family Plan', type: 'monthly', description: '3 benefits • 2 washes', note: 'The car wash membership will renew automatically...', priceLabel: '$29.99/mo', originalPriceLabel: '$39.99', price: 29.99, family_plan: true }
   * @param {Package} pkg - Package data used to derive labels, pricing, and benefits text.
   * @param {WashOptionType} type - Option type that determines whether the pricing is monthly or single.
   * @param {boolean} [familyPlan=false] - Whether to label the option as a family plan.
   * @returns {WashTierOption} A formatted wash tier option object ready for display.
   **/
  private buildOption(pkg: Package, type: WashOptionType, familyPlan = false): WashTierOption {
    const basePrice = type === 'monthly' ? pkg.monthly_list_price : pkg.single_list_price;
    const baseNum = parseFloat(String(basePrice ?? 0)) || 0;
    const finalPrice = Math.max(0, baseNum);

    const label = type === 'single' ? 'Single Wash' : familyPlan ? 'Family Plan' : 'Monthly';

    const benefitsCount = Array.isArray(pkg.features) ? pkg.features.length : 0;
    const benefitsText = `${benefitsCount} benefit${benefitsCount === 1 ? '' : 's'}`;

    const freq = pkg.wash_count_frequency ?? 0;
    const washText =
      type === 'single' ? '1 wash' : freq > 0 ? `${freq} wash${freq === 1 ? '' : 'es'}` : '';

    const description = washText ? `${benefitsText} • ${washText}` : benefitsText;

    let originalPriceLabel: string | undefined;
    if (type === 'single') {
      const strike =
        parseFloat(String(pkg.single_strikeoff_price ?? '').replace(/[^0-9.]/g, '')) || 0;
      if (strike > finalPrice + 0.001) {
        originalPriceLabel = `$${strike.toFixed(2)}`;
      }
    } else {
      const strike =
        parseFloat(String(pkg.monthly_strikeoff_price ?? '').replace(/[^0-9.]/g, '')) || 0;
      if (strike > finalPrice + 0.001) {
        originalPriceLabel = `$${strike.toFixed(2)}`;
      }
    }

    const priceLabel =
      type === 'monthly' ? `$${finalPrice.toFixed(2)}/mo` : `$${finalPrice.toFixed(2)}`;

    const note =
      type === 'monthly'
        ? 'The car wash membership will renew automatically. The customer can change the membership status any time from their membership settings in the Way App.'
        : undefined;

    return {
      id: `${pkg.id}-${type}${familyPlan ? '-family' : ''}`,
      label,
      type,
      description,
      note,
      priceLabel,
      originalPriceLabel,
      price: finalPrice,
      family_plan: familyPlan || pkg.family_plan,
    };
  }

  private findSelectedOption(): WashTierOption | null {
    const tier = this.washTiers.find(t => t.id === this.selectedTierId);
    return tier?.options.find(o => o.id === this.selectedOptionId) ?? null;
  }

  /**
   * Persist tier/option definitions on AddOrderStateService so later steps (payment, create order)
   * can resolve selectedPackageOption after this component is destroyed.
   */
  private copyWashTiersToState(): void {
    this.addOrderState.washTiers = this.washTiers.map(tier => ({
      ...tier,
      features: [...tier.features],
      options: tier.options.map(o => ({ ...o })),
    }));
  }
}
